import { cn } from '@/lib/utils';

export interface PinningContext {
  pinFirstColumn: boolean;
  selectable: boolean;
}

/**
 * Width of the checkbox gutter; the first data column pins right after it.
 *
 * `min-w-10` is load-bearing, not decoration. `width` on a cell is only a
 * preference in `table-layout: auto`, so a table wide enough to overflow gets
 * it squeezed down to the 16px checkbox — and then the first column, pinned at
 * a hard-coded `left-10`, is pushed 24px past where the table laid it out and
 * paints over its neighbour. Empresas showed «Dominio» as «ominio» because of
 * exactly this.
 */
export const SELECT_COLUMN_WIDTH = 'w-10 min-w-10';

/**
 * Sticky cells paint their own background, so they also have to repeat the
 * row's hover and selected states — otherwise the pinned column stays flat
 * while the rest of the row highlights.
 */
const STICKY_SURFACE =
  'bg-background group-hover/row:bg-muted/50 group-data-[selected=true]/row:bg-muted';

/**
 * The seam of the frozen column, drawn as a shadow rather than a border so it
 * sits on top of whatever scrolls underneath it. VISUAL §4.2 is explicit that
 * we fix the first column *and show the cut*; Nexor's own table hides it and
 * that is precisely the thing we said we would not copy.
 *
 * A hairline says "there is a column boundary here"; it does not say "text is
 * disappearing under me". So while there is content hidden on that side — the
 * scroll container announces it with `data-hidden-left` / `data-hidden-right` —
 * the seam lifts into a real shadow, which is the one gesture every table the
 * user already knows (Attio, Linear, Sheets) uses to mean exactly that.
 */
const STICKY_EDGE = cn(
  'shadow-[1px_0_0_0_var(--color-border)]',
  'group-data-[hidden-left=true]/scroll:shadow-[1px_0_0_0_var(--color-border),6px_0_8px_-6px_rgb(var(--foreground)/0.28)]',
);
const STICKY_EDGE_LEFT = cn(
  'shadow-[-1px_0_0_0_var(--color-border)]',
  'group-data-[hidden-right=true]/scroll:shadow-[-1px_0_0_0_var(--color-border),-6px_0_8px_-6px_rgb(var(--foreground)/0.28)]',
);

export function selectCellClass({ pinFirstColumn }: PinningContext): string {
  return cn(
    SELECT_COLUMN_WIDTH,
    'px-0 text-center',
    pinFirstColumn && cn('sticky left-0 z-20', STICKY_SURFACE),
  );
}

/**
 * The panel's own gutter, repeated by the first column.
 *
 * Page title, search box and footer all start at `px-6`; a cell padded `px-4`
 * starts its text 8px to the left of them, and the left edge of the panel reads
 * as broken. Only when the first column is the actual left edge — with a
 * checkbox rail in front of it, the alignment belongs to the rail.
 */
const FIRST_COLUMN_GUTTER = 'pl-6';

export function bodyCellClass(index: number, { pinFirstColumn, selectable }: PinningContext) {
  if (!pinFirstColumn || index !== 0) return undefined;
  return cn(
    'sticky z-20',
    selectable ? 'left-10' : cn('left-0', FIRST_COLUMN_GUTTER),
    STICKY_SURFACE,
    STICKY_EDGE,
  );
}

/** Header cells sit above body cells and never dim with the row. */
export function headCellClass(index: number, { pinFirstColumn, selectable }: PinningContext) {
  if (!pinFirstColumn || index !== 0) return undefined;
  return cn(
    'sticky z-30 bg-background',
    selectable ? 'left-10' : cn('left-0', FIRST_COLUMN_GUTTER),
    STICKY_EDGE,
  );
}

export const actionsCellClass = cn(
  'w-12 min-w-12 px-1 text-right sticky right-0 z-20',
  STICKY_SURFACE,
  STICKY_EDGE_LEFT,
);
export const actionsHeadClass = cn(
  'w-12 min-w-12 px-1 sticky right-0 z-30 bg-background',
  STICKY_EDGE_LEFT,
);
